import { PropsWithChildren, ReactNode } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import {
    useAnalyticsEvents,
    type UIAnalyticsEvent
} from "@atlaskit/analytics-next";
import Button from "@atlaskit/button";
import CrossIcon from "@atlaskit/icon/core/cross";
import ADSModal, { ModalTransition, useModal } from "@atlaskit/modal-dialog";
import { WidthNames } from "@atlaskit/modal-dialog/dist/types/internal/constants";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { TypographyStyles } from "@/libraries/wpl/foundations/src/typography-styles";

export interface ModalProps extends PropsWithChildren {
    title?: ReactNode;
    closeLabel?: string;
    closeModal: () => void;
    isOpen: boolean;
    width?: WidthNames;
}

/* Please use with a LinkModal to include the state/functionality */
export const Modal = ({
    title,
    closeLabel,
    isOpen,
    closeModal,
    width,
    children
}: ModalProps) => {
    return (
        <ModalTransition>
            {isOpen && (
                <ADSModal
                    onClose={closeModal}
                    shouldScrollInViewport
                    width={width}
                >
                    <TypographyStyles>
                        <CustomHeader title={title} closeLabel={closeLabel} />
                        <div css={mainContentStyle}>{children}</div>
                    </TypographyStyles>
                </ADSModal>
            )}
        </ModalTransition>
    );
};

const CustomHeader = ({
    title,
    closeLabel
}: Pick<ModalProps, "title" | "closeLabel">) => {
    const intl = useIntl();
    const { onClose, titleId } = useModal();
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const handleClose = (e: React.MouseEvent<HTMLElement>) => {
        createAnalyticsEvent({
            eventType: "ui",
            action: "clicked",
            actionSubject: "button",
            actionSubjectId: "closeModal",
            source: "modal",
            attributes: { label: "close modal" }
        }).fire();
        onClose?.(e, {} as UIAnalyticsEvent);
    };

    return (
        <div
            css={css({
                display: "grid",
                gridTemplateColumns: "1fr max-content",
                alignContent: "center",
                padding: token("space.400"),
                borderBlockEnd: `1px dashed ${wacToken(
                    "color.neutral.light300"
                )}`
            })}
        >
            <p className="card-title" css={titleStyle} id={titleId}>
                {title}
            </p>
            <Button appearance="subtle-link" onClick={handleClose}>
                <CrossIcon
                    label={
                        closeLabel ??
                        intl.formatMessage({
                            id: "Modal.closeIconLabel",
                            defaultMessage: "close modal"
                        })
                    }
                />
            </Button>
        </div>
    );
};

const mainContentStyle = css({
    padding: token("space.400")
});
const titleStyle = css({
    margin: 0
});
